import { useState, useEffect, type ClipboardEvent } from 'react';
import { Switch, Input, Button, Message, Typography, Card, Modal } from '@arco-design/web-react';
import { IconPhone, IconCheckCircle, IconCloseCircle } from '@arco-design/web-react/icon';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { endpoints } from '../../api/endpoints';
import { PhoneVerificationDialog } from '../PhoneVerificationDialog/PhoneVerificationDialog';
import './SMSSettings.css';

export const SMSSettings = () => {
  const { t } = useTranslation();
  
  const [smsEnabled, setSmsEnabled] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [loading, setLoading] = useState(false);
  const [verifyingPhone, setVerifyingPhone] = useState(false);
  const [showVerificationDialog, setShowVerificationDialog] = useState(false);
  /** null = show saved phone; string (including '') = user is editing */
  const [tempPhoneNumber, setTempPhoneNumber] = useState<string | null>(null);
  const [isChangingPhone, setIsChangingPhone] = useState(false);
  const [removingPhone, setRemovingPhone] = useState(false);

  // Load SMS preferences on component mount
  useEffect(() => {
    loadSMSPreferences();
  }, []);

  const loadSMSPreferences = async () => {
    try {
      const response = await axios.get(endpoints.sms.getPreferences);
      const enabled = response.data.sms_notifications_enabled || false;
      const verified = response.data.phone_verified || false;
      const phone = response.data.phone_number || '';
      
      setSmsEnabled(enabled);
      setPhoneNumber(phone);
      setPhoneVerified(verified);
    } catch (error) {
      console.error('Failed to load SMS preferences:', error);
    }
  };

  const handleSmsToggle = async (checked: boolean) => {
    // If not verified, keep toggle off until verification completes
    if (checked && !phoneVerified) {
      Message.info(t('sms.phone_verification_required'));
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(endpoints.sms.updatePreferences, {
        sms_notifications_enabled: checked,
      });

      setSmsEnabled(response.data.sms_notifications_enabled);
      Message.success(
        checked 
          ? t('sms.notifications_enabled') 
          : t('sms.notifications_disabled')
      );
    } catch (error: any) {
      console.error('Failed to update SMS preferences:', error);
      const errorData = error.response?.data;
      
      if (errorData?.verification_needed) {
        Message.info(t('sms.phone_verification_required'));
        // Phone section is already shown, so user can proceed with verification
      } else {
        Message.error(t('sms.update_failed'));
      }
    } finally {
      setLoading(false);
    }
  };

  const normalizeUsPhoneDigits = (value: string): string => {
    let digits = value.replace(/\D/g, '');
    if (digits.length === 11 && digits.startsWith('1')) {
      digits = digits.slice(1);
    }
    return digits.slice(0, 10);
  };

  const formatPhoneNumber = (value: string) => {
    const digits = normalizeUsPhoneDigits(value);

    if (digits.length <= 3) {
      return digits;
    }
    if (digits.length <= 6) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    }
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  };

  const toE164Format = (phone: string) => {
    const digits = normalizeUsPhoneDigits(phone);
    if (digits.length === 10) {
      return `+1${digits}`;
    }
    return '';
  };

  const handlePhoneChange = (value: string) => {
    setTempPhoneNumber(formatPhoneNumber(value));
  };

  const handlePhonePaste = (e: ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text');
    setTempPhoneNumber(formatPhoneNumber(pasted));
  };

  const handleChangePhoneClick = () => {
    setIsChangingPhone(true);
    setTempPhoneNumber(
      phoneNumber ? formatPhoneNumber(phoneNumber.replace('+1', '')) : ''
    );
  };

  const handleCancelPhoneChange = () => {
    setIsChangingPhone(false);
    setTempPhoneNumber(null);
    loadSMSPreferences();
  };

  const handleCancelVerification = () => {
    if (isChangingPhone) {
      handleCancelPhoneChange();
    } else {
      setTempPhoneNumber(null);
    }
  };

  const getDisplayedPhone = (): string => {
    if (tempPhoneNumber !== null) {
      return tempPhoneNumber;
    }
    if (!phoneNumber) {
      return '';
    }
    return formatPhoneNumber(phoneNumber.replace('+1', ''));
  };

  const isPhoneInputDisabled = phoneVerified && !isChangingPhone;

  const isPhoneUnchanged = () => {
    const e164 = toE164Format(getDisplayedPhone());
    return e164 === phoneNumber;
  };

  const handleStartVerification = async () => {
    const e164Phone = toE164Format(getDisplayedPhone());

    if (!e164Phone || e164Phone.length < 12) {
      Message.error(t('sms.invalid_phone_number'));
      return;
    }

    setVerifyingPhone(true);

    try {
      const response = await axios.post(endpoints.sms.startVerification, {
        phone_number: e164Phone,
        channel: 'sms',
      });

      if (response.data.status === 'pending') {
        setPhoneNumber(e164Phone);
        setShowVerificationDialog(true);
        Message.success(t('sms.verification_code_sent_success'));
      }
    } catch (error: any) {
      console.error('Failed to start verification:', error);
      const errorData = error.response?.data;
      if (errorData?.phone_already_in_use) {
        Message.error(t('sms.phone_already_in_use'));
        return;
      }
      const errorMessage = errorData?.error || t('sms.verification_start_failed');
      const twilioCode = errorData?.twilio_code;
      Message.error(twilioCode ? `${errorMessage} (Twilio ${twilioCode})` : errorMessage);
    } finally {
      setVerifyingPhone(false);
    }
  };

  const handleRemovePhone = () => {
    Modal.confirm({
      title: t('sms.remove_phone_confirm_title'),
      content: t('sms.remove_phone_confirm_message'),
      okText: t('sms.remove_phone_button'),
      cancelText: t('sms.cancel_change_phone_button'),
      onOk: async () => {
        setRemovingPhone(true);
        try {
          await axios.delete(endpoints.sms.removePhone);
          setPhoneNumber('');
          setPhoneVerified(false);
          setSmsEnabled(false);
          setIsChangingPhone(false);
          setTempPhoneNumber(null);
          setShowVerificationDialog(false);
          Message.success(t('sms.remove_phone_success'));
        } catch (error) {
          console.error('Failed to remove phone:', error);
          Message.error(t('sms.remove_phone_failed'));
        } finally {
          setRemovingPhone(false);
        }
      },
    });
  };

  const handleVerificationSuccess = async () => {
    setShowVerificationDialog(false);
    setIsChangingPhone(false);
    setTempPhoneNumber(null);
    setPhoneVerified(true);
    
    // Now that phone is verified, enable SMS notifications
    setLoading(true);
    try {
      const response = await axios.post(endpoints.sms.updatePreferences, {
        sms_notifications_enabled: true,
      });
      
      setSmsEnabled(response.data.sms_notifications_enabled);
      Message.success(t('sms.phone_verified_success') + ' ' + t('sms.notifications_enabled'));
    } catch (error) {
      console.error('Failed to enable SMS after verification:', error);
      Message.error(t('sms.update_failed'));
    } finally {
      setLoading(false);
    }
    
    loadSMSPreferences(); // Reload to get latest state
  };

  const getPhoneStatusIcon = () => {
    if (phoneVerified) {
      return <IconCheckCircle style={{ color: '#00b42a', fontSize: 18 }} />;
    }
    return <IconCloseCircle style={{ color: '#f53f3f', fontSize: 18 }} />;
  };

  const getPhoneStatusText = () => {
    if (phoneVerified) {
      return <span style={{ color: '#00b42a' }}>{t('sms.phone_verified')}</span>;
    }
    return <span style={{ color: '#f53f3f' }}>{t('sms.phone_not_verified')}</span>;
  };

  return (
    <Card 
      title={t('sms.settings_title')}
      style={{ marginBottom: 20 }}
      className="sms-settings-card"
    >
      <div className="sms-settings-card__toggle">
        <div className="sms-settings-card__toggle-copy">
          <span className="sms-settings-card__toggle-label">
            {t('sms.enable_notifications_label')}
          </span>
          <span className="sms-settings-card__toggle-help">
            {t('sms.enable_notifications_help')}
          </span>
        </div>
        <Switch
          className="sms-settings-card__toggle-switch"
          checked={smsEnabled}
          onChange={handleSmsToggle}
          loading={loading}
        />
      </div>

      <div className="sms-settings-card__compliance">
      <p className="sms-settings-card__compliance-paragraph">
        {t('sms.compliance_notice')}
      </p>
      </div>

      <div className="sms-settings-card__phone-section-static">
          <div className="sms-settings-card__phone-section">
            <Typography.Title heading={6} className="sms-settings-card__phone-heading">
              <IconPhone />
              {t('sms.phone_number_section')}
            </Typography.Title>

            <label className="sms-settings-card__phone-label">
              {t('sms.phone_number_label')}
            </label>
            <Input
              placeholder={t('sms.phone_number_placeholder')}
              value={getDisplayedPhone()}
              onChange={handlePhoneChange}
              onPaste={handlePhonePaste}
              disabled={isPhoneInputDisabled}
              prefix={<IconPhone />}
              suffix={getPhoneStatusIcon()}
              maxLength={14}
              inputMode="tel"
              autoComplete="tel"
            />
            <div className="sms-settings-card__phone-extra">
              {phoneVerified && !isChangingPhone
                ? getPhoneStatusText()
                : t('sms.phone_number_help')}
            </div>

            {phoneVerified && !isChangingPhone && (
              <Typography.Paragraph className="sms-settings-card__verified-message">
                <IconCheckCircle style={{ marginRight: 5 }} />
                {t('sms.phone_verified_message')}
              </Typography.Paragraph>
            )}

            {!phoneVerified && (
              <Typography.Paragraph className="sms-settings-card__note">
                {t('sms.verification_requirement_note')}
              </Typography.Paragraph>
            )}
            {isChangingPhone && phoneVerified && (
              <Typography.Paragraph className="sms-settings-card__note">
                {t('sms.change_phone_note')}
              </Typography.Paragraph>
            )}
          </div>

          <div className="sms-settings-card__phone-actions">
            {phoneVerified && !isChangingPhone && (
              <>
                <Button
                  type="primary"
                  shape="round"
                  className="sms-settings-card__action-btn sms-settings-card__action-btn--primary"
                  onClick={handleChangePhoneClick}
                >
                  {t('sms.change_phone_button')}
                </Button>
                <Button
                  type="default"
                  shape="round"
                  className="sms-settings-card__action-btn sms-settings-card__action-btn--cancel"
                  onClick={handleRemovePhone}
                  loading={removingPhone}
                >
                  {t('sms.remove_phone_button')}
                </Button>
              </>
            )}

            {phoneNumber && !phoneVerified && !isChangingPhone && (
              <Button
                type="default"
                shape="round"
                className="sms-settings-card__action-btn sms-settings-card__action-btn--cancel"
                onClick={handleRemovePhone}
                loading={removingPhone}
              >
                {t('sms.remove_phone_button')}
              </Button>
            )}

            {(!phoneVerified || isChangingPhone) && (
              <>
                <Button
                  type="primary"
                  shape="round"
                  className="sms-settings-card__action-btn sms-settings-card__action-btn--primary"
                  onClick={handleStartVerification}
                  loading={verifyingPhone}
                  disabled={
                    !getDisplayedPhone()
                    || getDisplayedPhone().replace(/\D/g, '').length !== 10
                    || (isChangingPhone && isPhoneUnchanged())
                  }
                >
                  {t('sms.verify_phone_button')}
                </Button>
                <Button
                  type="default"
                  shape="round"
                  className="sms-settings-card__action-btn sms-settings-card__action-btn--cancel"
                  onClick={handleCancelVerification}
                >
                  {t('sms.cancel_change_phone_button')}
                </Button>
              </>
            )}
          </div>
      </div>

      <PhoneVerificationDialog
        visible={showVerificationDialog}
        phoneNumber={phoneNumber}
        onSuccess={handleVerificationSuccess}
        onCancel={() => setShowVerificationDialog(false)}
      />
    </Card>
  );
};

